import { Button, Card, Col, Image, Input, Row, Select, Typography } from "antd";
import { getPrimaryMediaUrl } from "commons/helper";

const { Text } = Typography;

/**
 * Controlled component for event amenities / activities Form.Item.
 *
 * Props injected by Form.Item:
 *   value    – array of item objects
 *   onChange – called with the updated array
 *
 * Own props:
 *   masterItems     – raw master list from the API
 *   nameKey         – field name that holds the display name on each master item
 *   urlKey          – field name to store the resolved image URL in each value item
 *                     ("icon_url" for amenities, "media_url" for activities)
 *   loading         – whether master data is still loading
 *   placeholder     – select placeholder text
 *   showDescription – show the editable description textarea (default false)
 */
function EventItemSelector({
  value = [],
  onChange,
  masterItems = [],
  nameKey,
  urlKey,
  loading,
  placeholder,
  showDescription = false,
}) {
  const selectedNames = new Set(value.map((v) => v.name));

  const availableOptions = masterItems
    .filter((item) => !selectedNames.has(item[nameKey]))
    .map((item) => ({
      value: item[nameKey],
      label: (
        <Row align="middle" justify="space-between">
          <Col>{item[nameKey]}</Col>
          <Col>
            <Image
              width={20}
              height={20}
              preview={false}
              src={getPrimaryMediaUrl(item.media)}
              alt={item[nameKey]}
            />
          </Col>
        </Row>
      ),
    }));

  const handleAdd = (name) => {
    const masterItem = masterItems.find((m) => m[nameKey] === name);
    const imageUrl = getPrimaryMediaUrl(masterItem?.media) ?? "";
    const newItem = { name, [urlKey]: imageUrl };
    if (showDescription) newItem.description = "";
    onChange([...value, newItem]);
  };

  const handleRemove = (name) => {
    onChange(value.filter((v) => v.name !== name));
  };

  const handleDescriptionChange = (name, description) => {
    onChange(value.map((v) => (v.name === name ? { ...v, description } : v)));
  };

  return (
    <>
      <Select
        placeholder={placeholder}
        options={availableOptions}
        loading={loading}
        value={null}
        onChange={handleAdd}
        style={{ width: "100%", marginBottom: value.length ? 12 : 0 }}
        showSearch
      />

      {value.map((item) => {
        const imageUrl = item[urlKey];
        const descriptionMissing = showDescription && !item.description?.trim();

        return (
          <Card
            key={item.name}
            size="small"
            style={{ marginBottom: 8, borderRadius: 8 }}
            styles={{ body: { padding: "10px 12px" } }}
          >
            <Row
              gutter={10}
              align="middle"
              style={{ marginBottom: showDescription ? 8 : 0 }}
            >
              {imageUrl && (
                <Col>
                  <img
                    src={imageUrl}
                    alt={item.name}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 6,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Col>
              )}
              <Col flex={1}>
                <Text strong>{item.name}</Text>
              </Col>
              <Col>
                <Button
                  type="text"
                  danger
                  size="small"
                  onClick={() => handleRemove(item.name)}
                >
                  ✕
                </Button>
              </Col>
            </Row>

            {showDescription && (
              <>
                <Input.TextArea
                  value={item.description}
                  onChange={(e) =>
                    handleDescriptionChange(item.name, e.target.value)
                  }
                  placeholder="Brief description…"
                  rows={2}
                  maxLength={500}
                  showCount
                  status={descriptionMissing ? "error" : ""}
                />
                {descriptionMissing && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    Description is required.
                  </Text>
                )}
              </>
            )}
          </Card>
        );
      })}
    </>
  );
}

export default EventItemSelector;
